import React, { useState, useCallback, useEffect } from 'react';
import { Paper, Switch, FormControlLabel, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { writeAdminArticleStream, getAdminEditorialAreas, getNeoFeedContextSuggestions } from '../../../utils/api';
import { createNotification } from '../../../utils/alerts';
import { getErrorMessage } from '../../../utils/errorUtils';
import ArticleWriter from '../../../components/admin/ArticleWriter';
import NeoFeedSuggestionsPanel from '../../../components/admin/ArticleWriter/NeoFeedSuggestionsPanel';
import useNeoFeedContextSelection from '../../../components/admin/ArticleWriter/useNeoFeedContextSelection';
import { buildNeoFeedContextSourcesPayload } from '../../../components/admin/ArticleWriter/neoFeedContextUtils';

const PRIMARY = '#f25d27';

/* ─── Página: Escrever matéria (independente de Inteligência Editorial) ── */
export default function AdminArticlesWritePage() {
    const navigate  = useNavigate();
    const isMobile  = useMediaQuery('(max-width:900px)');

    // Validação só é exigida antes da primeira geração bem-sucedida
    const [hasWrittenOnce, setHasWrittenOnce] = useState(false);

    // Áreas editoriais disponíveis para definir o tom de escrita da matéria
    const [editorialAreas, setEditorialAreas] = useState([]);
    const [editorialAreasLoading, setEditorialAreasLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const data = await getAdminEditorialAreas({ page_size: 100 });
                if (active) setEditorialAreas(data?.editorial_areas || []);
            } catch (err) {
                if (active) {
                    createNotification('error', getErrorMessage(err, 'Erro ao carregar áreas editoriais.'), isMobile);
                }
            } finally {
                if (active) setEditorialAreasLoading(false);
            }
        })();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Id da matéria criada na primeira geração — a partir dela, toda
    // reescrita informa `article_id` para gravar uma nova versão da mesma
    // matéria, em vez de criar uma matéria independente nova. Também é o
    // identificador usado para buscar sugestões no acervo NeoFeed.
    const [articleId, setArticleId] = useState(null);

    /* ── Sugestões NeoFeed (contexto opcional, anexável via modal de
       contexto do composer) — só é possível buscar depois que a primeira
       versão é gerada (é preciso um `article_id`). ── */
    const [neoFeedEnabled, setNeoFeedEnabled] = useState(true);
    const [neoFeedSuggestions, setNeoFeedSuggestions] = useState([]);
    const [neoFeedLoading, setNeoFeedLoading] = useState(false);
    const [neoFeedError, setNeoFeedError] = useState(null);
    const {
        selectedSources: selectedNeoFeedSources,
        toggleSuggestion: handleToggleNeoFeedSuggestion,
        removeSource: handleRemoveNeoFeedSource,
        toggleChunk: handleToggleNeoFeedChunk,
        changeUsageMode: handleChangeNeoFeedUsageMode,
        reset: resetNeoFeedSelection,
    } = useNeoFeedContextSelection({ forceUsageMode: 'factual' });

    const fetchNeoFeedSuggestions = useCallback(async (articleIdOverride) => {
        const effectiveArticleId = articleIdOverride ?? articleId;
        if (!effectiveArticleId) return;

        setNeoFeedLoading(true);
        setNeoFeedError(null);
        try {
            const data = await getNeoFeedContextSuggestions({ article_id: effectiveArticleId });
            setNeoFeedSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
        } catch (err) {
            const message = getErrorMessage(err, 'Erro ao buscar sugestões no acervo NeoFeed.');
            setNeoFeedError(message);
            if (err.response?.status !== 401) {
                createNotification('error', message, isMobile);
            }
        } finally {
            setNeoFeedLoading(false);
        }
    }, [articleId, isMobile]);

    const handleToggleNeoFeedEnabled = useCallback((event) => {
        const checked = event.target.checked;
        setNeoFeedEnabled(checked);
        if (checked) {
            if (articleId) fetchNeoFeedSuggestions();
        } else {
            setNeoFeedSuggestions([]);
            setNeoFeedError(null);
            resetNeoFeedSelection();
        }
    }, [articleId, fetchNeoFeedSuggestions, resetNeoFeedSelection]);

    const canGenerate = useCallback(({ instruction, attachments }) => {
        if (hasWrittenOnce) return true;
        return Boolean(instruction) || attachments.length > 0;
    }, [hasWrittenOnce]);

    const handleValidationBlocked = useCallback(() => {
        createNotification(
            'error',
            'Adicione instruções ou pelo menos um anexo válido para gerar a matéria.',
            isMobile
        );
    }, [isMobile]);

    const streamFn = useCallback((payload, options) => (
        writeAdminArticleStream({ ...payload, article_id: articleId }, options)
    ), [articleId]);

    const handleGenerated = useCallback((data) => {
        setHasWrittenOnce(true);

        let effectiveArticleId = articleId;
        if (!articleId) {
            const newId = data?.article_id ?? data?.version_id ?? null;
            if (newId) {
                setArticleId(newId);
                effectiveArticleId = newId;
            }
        }

        if (neoFeedEnabled) {
            fetchNeoFeedSuggestions(effectiveArticleId);
        }
    }, [articleId, neoFeedEnabled, fetchNeoFeedSuggestions]);

    const neoFeedContextSourcesPayload = buildNeoFeedContextSourcesPayload(selectedNeoFeedSources);
    const extraPayload = neoFeedContextSourcesPayload.length
        ? { neofeed_context_sources: JSON.stringify(neoFeedContextSourcesPayload) }
        : undefined;

    const neoFeedToggleControl = (
        <Paper sx={{ borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', p: '6px 14px', mb: 1 }}>
            <FormControlLabel
                control={
                    <Switch
                        size="small"
                        checked={neoFeedEnabled}
                        onChange={handleToggleNeoFeedEnabled}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY },
                        }}
                    />
                }
                label={
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#333' }}>
                        Usar base NeoFeed
                    </Typography>
                }
                sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
            />
        </Paper>
    );

    const selectedNeoFeedCount = Object.keys(selectedNeoFeedSources).length;
    const contextSources = [
        {
            key: 'neofeed',
            label: 'Sugestões NeoFeed',
            selectedCount: selectedNeoFeedCount,
            summaryItems: Object.values(selectedNeoFeedSources)
                .map((entry) => entry.suggestion?.title)
                .filter(Boolean),
            onClearAll: resetNeoFeedSelection,
            topControl: neoFeedToggleControl,
            panel: neoFeedEnabled ? (
                <NeoFeedSuggestionsPanel
                    embedded
                    expanded
                    onToggleExpanded={() => {}}
                    suggestions={neoFeedSuggestions}
                    loading={neoFeedLoading}
                    error={neoFeedError}
                    selectedSources={selectedNeoFeedSources}
                    onToggleSuggestion={handleToggleNeoFeedSuggestion}
                    onToggleChunk={handleToggleNeoFeedChunk}
                    onChangeUsageMode={handleChangeNeoFeedUsageMode}
                    onRemoveSource={handleRemoveNeoFeedSource}
                    emptyMessage={articleId
                        ? 'Nenhuma sugestão encontrada para esta versão.'
                        : 'Gere a primeira versão para ver sugestões do acervo NeoFeed.'}
                />
            ) : (
                <Typography sx={{ fontSize: '0.82rem', color: '#999', textAlign: 'center', py: 3 }}>
                    Ative "Usar base NeoFeed" acima para ver sugestões do acervo.
                </Typography>
            ),
        },
    ];

    return (
        <ArticleWriter
            backLabel="Voltar as matérias"
            onBack={() => navigate('/admin/articles')}
            loading={false}
            initialArticlesHistory={[]}
            initialArticle={null}
            initialResponseId={null}
            streamFn={streamFn}
            extraPayload={extraPayload}
            autoGenerateWhenEmpty={false}
            canGenerate={canGenerate}
            onValidationBlocked={handleValidationBlocked}
            generateBlockedTooltip="Adicione instruções ou um anexo válido para continuar"
            onGenerated={handleGenerated}
            editorialAreas={editorialAreas}
            editorialAreasLoading={editorialAreasLoading}
            contextSources={contextSources}
        />
    );
}
